import React, { useState, useEffect } from 'react';
import { Plus, Sparkles, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import HabitCard from '../components/HabitCard';
import AddHabitModal from '../components/AddHabitModal';
import EditHabitModal from '../components/EditHabitModal';
import StatsOverview from '../components/StatsOverview';
import TrialWelcomeModal from '../components/TrialWelcomeModal';
import MotivationalQuote from '../components/MotivationalQuote';
import ProgressMilestones from '../components/ProgressMilestones';
import DraggableHabitList from '../components/DraggableHabitList';
import ShareToSocial from '../components/ShareToSocial';
import AnnouncementBanner from '../components/AnnouncementBanner';
import { getHabits, deleteHabit } from '../lib/storage';
import { subscriptionAPI, habitsAPI, logsAPI } from '../services/api';
import { toast } from 'sonner';
import { triggerAllHabitsComplete } from '../lib/confetti';

const Dashboard = () => {
  const [habits, setHabits] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedHabit, setSelectedHabit] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(7);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareStats, setShareStats] = useState({ completedToday: 0, currentStreak: 0 });

  useEffect(() => {
    loadHabits();
  }, [refreshKey]);

  // Check for trial welcome modal on first login
  useEffect(() => {
    const checkTrialWelcome = async () => {
      const hasSeenWelcome = localStorage.getItem('habit2goals_seen_welcome');
      if (hasSeenWelcome) return;

      try {
        const status = await subscriptionAPI.getStatus();
        if (status.status === 'trial') {
          setTrialDaysRemaining(status.trial_days_remaining || 7);
          setShowTrialModal(true);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }
    };

    checkTrialWelcome();
  }, []);

  const loadHabits = async () => {
    try {
      setLoading(true);
      const loadedHabits = await getHabits();
      setHabits(loadedHabits);
      
      // Update share stats
      const logs = await logsAPI.getAll();
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const completedToday = logs.filter(l => l.date === todayStr).length;
      
      setShareStats({
        completedToday,
        currentStreak: 0, // Will be calculated from milestones
      });
      
      // Check if all habits completed for today
      if (loadedHabits.length > 0 && completedToday === loadedHabits.length) {
        triggerAllHabitsComplete();
      }
    } catch (error) {
      console.error('Error loading habits:', error);
      toast.error('Failed to load habits');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHabit = async (habitId) => {
    if (window.confirm('Are you sure you want to delete this habit?')) {
      try {
        await deleteHabit(habitId);
        toast.success('Habit deleted successfully');
        loadHabits();
      } catch (error) {
        console.error('Error deleting habit:', error);
        toast.error('Failed to delete habit');
      }
    }
  };

  const handleEditHabit = (habit) => {
    setSelectedHabit(habit);
    setIsEditModalOpen(true);
  };

  const handleHabitAdded = () => {
    setIsModalOpen(false); // Ensure modal closes
    loadHabits();
    setRefreshKey((prev) => prev + 1);
  };

  const handleHabitUpdated = () => {
    setIsEditModalOpen(false); // Ensure modal closes
    setSelectedHabit(null);
    loadHabits();
    setRefreshKey((prev) => prev + 1);
  };

  const handleCloseTrialModal = () => {
    localStorage.setItem('habit2goals_seen_welcome', 'true');
    setShowTrialModal(false);
  };

  const handleToggle = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleReorder = async (newHabits) => {
    setHabits(newHabits);
    try {
      await habitsAPI.reorder(newHabits.map(h => h.id));
    } catch (error) {
      console.error('Error reordering habits:', error);
      toast.error('Failed to save order');
      loadHabits(); // Reload to restore original order
    }
  };

  const handleAddNote = (habit, existingNote) => {
    // Could open a note editing modal here
    console.log('Add note to habit:', habit.id, existingNote);
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading habits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Announcement Banner */}
      <AnnouncementBanner />

      {/* Motivational Quote */}
      <MotivationalQuote />

      {/* Hero Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold gradient-text mb-2">
            Today's Focus
          </h2>
          <p className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            {today}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowShareModal(true)}
            variant="outline"
            data-testid="share-progress-button"
            className="gap-2"
          >
            <Share2 className="w-4 h-4" />
            Share Progress
          </Button>
          <Button
            onClick={() => setIsModalOpen(true)}
            data-testid="add-habit-button"
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover-glow transition-all duration-200 gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Habit
          </Button>
        </div>
      </div>

      {/* Progress Milestones */}
      <ProgressMilestones key={`milestones-${refreshKey}`} />

      {/* Stats Overview */}
      <StatsOverview key={refreshKey} />

      {/* Habits List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-2xl font-semibold text-slate-800 dark:text-slate-100">
            My Habits
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Drag to reorder
          </p>
        </div>
        
        {habits.length === 0 ? (
          <div 
            className="flex flex-col items-center justify-center py-16 px-4 gradient-card rounded-2xl border-2 border-dashed border-blue-300 dark:border-blue-700"
            data-testid="empty-state"
          >
            <div className="w-20 h-20 mb-4 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
              <Plus className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h4 className="font-heading text-xl font-semibold text-slate-700 dark:text-slate-200 mb-2">
              No habits yet
            </h4>
            <p className="text-slate-500 dark:text-slate-400 text-center mb-6 max-w-sm">
              Start building better habits today. Choose from our templates or create your own!
            </p>
            <Button
              onClick={() => setIsModalOpen(true)}
              data-testid="empty-state-add-button"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white gap-2 shadow-lg hover-glow"
            >
              <Plus className="w-4 h-4" />
              Add Your First Habit
            </Button>
          </div>
        ) : (
          <DraggableHabitList
            habits={habits}
            onDelete={handleDeleteHabit}
            onEdit={handleEditHabit}
            onToggle={handleToggle}
            onAddNote={handleAddNote}
            onReorder={handleReorder}
          />
        )}
      </div>

      {/* Add Habit Modal */}
      <AddHabitModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onHabitAdded={handleHabitAdded}
      />

      {/* Edit Habit Modal */}
      <EditHabitModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedHabit(null);
        }}
        habit={selectedHabit}
        onHabitUpdated={handleHabitUpdated}
      />

      {/* Trial Welcome Modal */}
      <TrialWelcomeModal
        isOpen={showTrialModal}
        onClose={handleCloseTrialModal}
        daysRemaining={trialDaysRemaining}
      />

      {/* Share Modal */}
      {showShareModal && (
        <ShareToSocial
          stats={shareStats}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
